import { useMemo } from 'react';
import { differenceInDays } from 'date-fns';

import { Severity, ErrorType } from '@atlassian/growth-commons-error-analytics';

import type { ConfluenceFreePoolTargetExperimentQuery$data } from '../pre-upgrade-modal-graphql-query/__generated__/ConfluenceFreePoolTargetExperimentQuery.graphql';
import { addAtlOriginToUrl } from '../utils';
import { useExperimentErrorAnalytics } from '../utils/useExperimentErrorAnalytics';

export const useIsEligibleForExperiment = (
	data: ConfluenceFreePoolTargetExperimentQuery$data | undefined,
): {
	isEligible: boolean;
	experienceUrl: string;
	analyticsAttributes: Record<string, any>;
} => {
	const { trackError } = useExperimentErrorAnalytics();

	const entitlement = data?.tenantContexts?.[0]?.entitlementInfo?.entitlement;

	let hasNoTrialHistory: boolean | undefined;
	let hasOnlyReverseTrialedWithoutPaidFeatureUsage: boolean | undefined;
	let hasPaymentDetailsEntered: boolean | undefined;
	let trialTimeLeft: number | undefined;
	let isInPredunning: boolean | undefined;
	let licensedUsersRaw: number | undefined;
	let createdAt: number | undefined;
	let accountAgeDays: number | undefined;
	let isEligible: boolean = false;
	let isAvailableToUser: boolean | undefined;
	let experienceUrl: string = '';

	try {
		hasNoTrialHistory = (entitlement?.entitlementProfile?.trialHistory ?? []).length === 0;

		hasOnlyReverseTrialedWithoutPaidFeatureUsage =
			(entitlement?.entitlementProfile?.trialHistory ?? []).length === 1 &&
			entitlement?.entitlementProfile?.trialHistory[0].trialType === 'REVERSE_TRIAL' &&
			entitlement?.entitlementProfile?.paidFeatureUsage?.length === 0;

		hasPaymentDetailsEntered = Boolean(entitlement?.transactionAccount?.isBillToPresent);
		trialTimeLeft = entitlement?.subscription?.trial?.timeLeft ?? 0;
		isInPredunning = entitlement?.preDunning?.status === 'IN_PRE_DUNNING';
		licensedUsersRaw = entitlement?.latestUsageForChargeElement ?? 1;
		createdAt = entitlement?.createdAt ?? Date.now();
		accountAgeDays = differenceInDays(new Date(), new Date(createdAt));

		isEligible = Boolean(
			(hasNoTrialHistory || hasOnlyReverseTrialedWithoutPaidFeatureUsage) &&
				licensedUsersRaw >= 2 &&
				!isInPredunning &&
				trialTimeLeft === 0 &&
				hasPaymentDetailsEntered &&
				accountAgeDays > 120,
		);

		isAvailableToUser =
			entitlement?.experienceCapabilities?.changeOfferingV2?.isAvailableToUser ?? false;

		experienceUrl = isAvailableToUser
			? addAtlOriginToUrl(
					entitlement?.experienceCapabilities?.changeOfferingV2?.experienceUrl ?? '',
				)
			: '';
	} catch (error) {
		trackError({
			errorType: ErrorType.GENERIC,
			errorMessage:
				error instanceof Error ? error.message : 'Experiment eligibility determination failed',
			severity: Severity.MEDIUM,
			source: 'useIsEligibleForExperiment_eligibilityCalculation',
			cohort: undefined,
			additionalAttributes: {
				hasEntitlementData: Boolean(entitlement),
				dataKeys: entitlement ? Object.keys(entitlement) : [],
				errorStack: error instanceof Error ? error.stack : undefined,
				errorName: error instanceof Error ? error.name : undefined,
			},
		});
	}

	const memoizedAnalyticsAttributes = useMemo(() => {
		return {
			hasNoTrialHistory,
			hasOnlyReverseTrialedWithoutPaidFeatureUsage,
			trialTimeLeft,
			isInPredunning,
			accountAgeDays,
			licensedUsers: licensedUsersRaw,
			experienceUrl,
		};
	}, [
		hasNoTrialHistory,
		hasOnlyReverseTrialedWithoutPaidFeatureUsage,
		trialTimeLeft,
		isInPredunning,
		accountAgeDays,
		licensedUsersRaw,
		experienceUrl,
	]);

	return {
		isEligible,
		experienceUrl,
		analyticsAttributes: memoizedAnalyticsAttributes,
	};
};
